// ─── Modo oscuro para TODOS los correos de mezo ─────────────────────────────
//
// EL PROBLEMA (reportado con un recibo real en el celular):
// Las plantillas declaraban `color-scheme: light` y nada más. La app de Gmail
// en Android IGNORA eso cuando el correo no ofrece estilos oscuros propios:
// invierte los colores ella misma, a lo bruto. Y estos diseños tienen
// contraste a propósito (banda negra arriba, cuerpo crema), así que la
// inversión los daba vuelta — la banda salía clara y el cuerpo oscuro. En el
// computador se veían bien porque ahí Gmail no invierte.
//
// LA SALIDA: declarar `light dark` y ENTREGAR una versión oscura hecha por
// nosotros. Como la marca ya es oscura (tinta #080706 + oro + crema), el correo
// en modo oscuro no queda "invertido": queda como se ve la app.
//
// CÓMO, sin classear elemento por elemento (que es frágil y puede romper el
// HTML de 7 plantillas): un bloque <style> que remapea los COLORES EXACTOS de
// la marca por selector de atributo `[style*="..."]`. Una regla con !important
// gana sobre el estilo inline, así que no hace falta tocar cada <td>. Depende
// de que los inline usen SIEMPRE la misma forma sin espacio (`color:#2B2420`),
// que es como está escrito todo el set — verificado.
//
// También se incluyen las reglas por CLASE (`.mz-*`) que usa el recibo, para
// que este módulo sea la ÚNICA fuente de verdad: quien agregue un correo nuevo
// solo pega ESTILO_MODO_OSCURO + META_MODO_OSCURO y queda cubierto, use clases
// o no.
//
// LÍMITE HONESTO: Outlook.com (data-ogsc) reescribe los inline, así que los
// selectores de atributo no lo alcanzan; ahí solo funcionan las clases (el
// recibo las tiene). Para el resto, prefers-color-scheme cubre lo que importa:
// Gmail app, Apple Mail e iOS — que es donde se vio el problema.

// Mapa de la paleta clara → oscura. Fondos que se oscurecen, textos que se
// aclaran, bordes que se apagan. Lo que NO está aquí se deja igual a propósito:
// el oro (#C8903F/#B8802F) y las bandas oscuras (#080706) ya se ven bien sobre
// tinta, y el texto claro de las bandas (#F4ECD8/#B8A88E) debe seguir claro.
const FONDOS = {
  '#E8E4DB': '#050403',  // fondo de la página
  '#f6f3ec': '#050403',  // ídem, paleta de las edge functions
  '#F4ECD8': '#14110E',  // tarjeta y cuerpo (mismo tono que ya usa el recibo)
  '#fff':    '#14110E',  // tarjeta en las edge functions (cubre también #ffffff:
  '#fafaf6': '#14110E',  //   el selector es por SUBCADENA, y ambos van al mismo tono)
  '#EBE2C9': '#0C0A08',  // pie
  '#EDF7F1': '#0E1A14',  // caja verde (info de referidos)
};
// Se conserva la jerarquía, no solo el tono: en claro la página y las cajas
// internas comparten el crema (#f6f3ec) y la tarjeta es blanca. Al mandar la
// página y las cajas a #050403 y la tarjeta a #14110E, las cajas siguen
// leyendo como hundidas dentro de la tarjeta en vez de fundirse con ella.
const TEXTOS = {
  '#2B2420': '#F4ECD8',  // texto principal (casi negro → crema)
  '#1a1612': '#F4ECD8',  // ídem, paleta de las edge functions
  '#666':    '#A2937E',  // gris suelto de las edge functions
  '#3A322A': '#E0D5BF',  // texto de cuerpo (un punto más suave que el crema)
  '#7A6A58': '#A2937E',  // texto tenue
  '#8A7A66': '#A2937E',  // tenue (variante del pie)
  '#8A6D2F': '#E4B878',  // texto de la caja ámbar
  '#8A6612': '#E4B878',  // ámbar (aviso-vencimiento)
  '#2E8450': '#7FD1A0',  // texto de la caja verde
};
const BORDES = {
  '#E8DDC2': '#2A2520',  // borde claro sobre crema → línea oscura
  '#eae5d6': '#2A2520',  // ídem en el reporte diario. Sin esto queda una raya
                         // crema brillante cruzando la tarjeta oscura.
};

// Reglas por CLASE — las que usa el recibo (belt-and-suspenders: si el correo
// trae clases, funcionan aunque el cliente reescriba los inline, p.ej. Outlook).
const CLASES = {
  '.mz-fondo':   'background:#050403 !important;',
  '.mz-tarjeta': 'background:#14110E !important;',
  '.mz-panel':   'background:#14110E !important;',
  '.mz-pie':     'background:#0C0A08 !important; border-top-color:#2A2520 !important;',
  '.mz-texto':   'color:#F4ECD8 !important;',
  '.mz-tenue':   'color:#A2937E !important;',
  '.mz-oro':     'color:#D9A437 !important;',
  '.mz-linea':   'border-bottom-color:#2A2520 !important;',
  '.mz-borde':   'border-top-color:#2A2520 !important;',
};

function reglas() {
  const out = [];
  for (const [claro, oscuro] of Object.entries(FONDOS)) {
    out.push(`[style*="background:${claro}"]{background:${oscuro} !important;}`);
  }
  for (const [claro, oscuro] of Object.entries(TEXTOS)) {
    out.push(`[style*="color:${claro}"]{color:${oscuro} !important;}`);
  }
  for (const [claro, oscuro] of Object.entries(BORDES)) {
    out.push(`[style*="${claro}"]{border-color:${oscuro} !important;}`);
  }
  for (const [clase, decl] of Object.entries(CLASES)) {
    out.push(`${clase}{${decl}}`);
  }
  return out.join('\n    ');
}

// El bloque <style> completo. Va en el <head>. Las mismas reglas viven en el
// @media (para clientes que respetan prefers-color-scheme) y bajo [data-ogsc]
// (el gancho de Outlook.com; ahí solo pegan las de CLASE, no las de atributo).
export const ESTILO_MODO_OSCURO = `<style>
  :root { color-scheme: light dark; supported-color-schemes: light dark; }
  @media (prefers-color-scheme: dark) {
    ${reglas()}
  }
  [data-ogsc] .mz-fondo   { background:#050403 !important; }
  [data-ogsc] .mz-tarjeta { background:#14110E !important; }
  [data-ogsc] .mz-panel   { background:#14110E !important; }
  [data-ogsc] .mz-pie     { background:#0C0A08 !important; border-top-color:#2A2520 !important; }
  [data-ogsc] .mz-texto   { color:#F4ECD8 !important; }
  [data-ogsc] .mz-tenue   { color:#A2937E !important; }
  [data-ogsc] .mz-oro     { color:#D9A437 !important; }
  [data-ogsc] .mz-linea   { border-bottom-color:#2A2520 !important; }
  [data-ogsc] .mz-borde   { border-top-color:#2A2520 !important; }
</style>`;

// Los dos <meta> que le dicen al cliente "esta plantilla SÍ tiene modo oscuro,
// no lo inventes tú". Sin esto, Gmail móvil invierte por su cuenta.
export const META_MODO_OSCURO =
  '<meta name="color-scheme" content="light dark" />\n  <meta name="supported-color-schemes" content="light dark" />';
